
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, 
  Calendar, 
  Target, 
  BookOpen, 
  Clock,
  Users,
  Award
} from 'lucide-react';
import { useUserProgress, useUserGamification } from '@/hooks/useUserProgress';
import { useAuth } from '@/hooks/useAuth';
import { formatDistanceToNow, subWeeks, subMonths, isAfter } from 'date-fns';

const ProgressAnalytics: React.FC = () => {
  const { user } = useAuth();
  const { data: progressData } = useUserProgress();
  const { data: gamificationData } = useUserGamification();

  if (!user || !progressData) {
    return null;
  }

  // Calculate weekly stats
  const weekAgo = subWeeks(new Date(), 1);
  const monthAgo = subMonths(new Date(), 1);

  const weeklyProgress = progressData.filter(p => 
    p.last_accessed_at && isAfter(new Date(p.last_accessed_at), weekAgo)
  );

  const monthlyProgress = progressData.filter(p => 
    p.last_accessed_at && isAfter(new Date(p.last_accessed_at), monthAgo)
  );

  const weeklyCompletions = weeklyProgress.filter(p => p.status === 'completed').length;
  const monthlyCompletions = monthlyProgress.filter(p => p.status === 'completed').length;
  const weeklyTimeSpent = weeklyProgress.reduce((acc, p) => acc + (p.time_spent_seconds || 0), 0);
  const monthlyTimeSpent = monthlyProgress.reduce((acc, p) => acc + (p.time_spent_seconds || 0), 0);

  // Calculate learning velocity (completions per week)
  const learningVelocity = weeklyCompletions;
  const averageSessionTime = weeklyProgress.length > 0 ? 
    Math.floor(weeklyTimeSpent / weeklyProgress.length / 60) : 0;

  // Mock comparison data (in real implementation, this would come from analytics)
  const averageUserCompletions = 3;
  const percentileRank = Math.min(95, Math.max(5, (weeklyCompletions / averageUserCompletions) * 50 + 25));

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="weekly" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="weekly">Weekly Report</TabsTrigger>
          <TabsTrigger value="monthly">Monthly Report</TabsTrigger>
        </TabsList>

        <TabsContent value="weekly" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <Target className="h-4 w-4 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold">{weeklyCompletions}</p>
                    <p className="text-xs text-black">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{formatTime(weeklyTimeSpent)}</p>
                    <p className="text-xs text-black">Time Spent</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-4 w-4 text-purple-500" />
                  <div>
                    <p className="text-2xl font-bold">{learningVelocity}</p>
                    <p className="text-xs text-black">Weekly Velocity</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <BookOpen className="h-4 w-4 text-orange-500" />
                  <div>
                    <p className="text-2xl font-bold">{averageSessionTime}m</p>
                    <p className="text-xs text-black">Avg Session</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Performance Comparison */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Performance Comparison
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Your Performance</span>
                  <span>{percentileRank.toFixed(0)}th percentile</span>
                </div>
                <Progress value={percentileRank} className="h-2" />
                <p className="text-xs text-black">
                  You're performing better than {percentileRank.toFixed(0)}% of users this week
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-lg font-semibold">{weeklyCompletions}</p>
                  <p className="text-xs text-black">Your Completions</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-lg font-semibold">{averageUserCompletions}</p>
                  <p className="text-xs text-black">Average User</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current Streak */}
          {gamificationData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  Learning Streak
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center space-y-2">
                  <p className="text-3xl font-bold text-orange-500">
                    {gamificationData.current_streak}
                  </p>
                  <p className="text-sm text-black">Day streak</p>
                  <Badge variant="outline">
                    Best: {gamificationData.longest_streak} days
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="monthly" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <Target className="h-4 w-4 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold">{monthlyCompletions}</p>
                    <p className="text-xs text-black">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{formatTime(monthlyTimeSpent)}</p>
                    <p className="text-xs text-black">Time Spent</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <BookOpen className="h-4 w-4 text-purple-500" />
                  <div>
                    <p className="text-2xl font-bold">{monthlyProgress.length}</p>
                    <p className="text-xs text-black">Items Accessed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-4 w-4 text-orange-500" />
                  <div>
                    <p className="text-2xl font-bold">
                      {monthlyProgress.length > 0 ? Math.round((monthlyCompletions / monthlyProgress.length) * 100) : 0}%
                    </p>
                    <p className="text-xs text-black">Completion Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly insights */}
          <Card>
            <CardHeader>
              <CardTitle>Monthly Insights</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Completion Rate</span>
                <span className="text-sm font-medium">
                  {monthlyProgress.length > 0 ? Math.round((monthlyCompletions / monthlyProgress.length) * 100) : 0}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Average Daily Time</span>
                <span className="text-sm font-medium">
                  {formatTime(Math.floor(monthlyTimeSpent / 30))}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Most Active Day</span>
                <span className="text-sm font-medium">
                  {gamificationData?.last_activity_date ? 
                    formatDistanceToNow(new Date(gamificationData.last_activity_date), { addSuffix: true }) :
                    'No data'
                  }
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProgressAnalytics;
